import { useEffect, useMemo, useState } from 'react';
import type { MessengerPinnedMessage } from '@/api/client';
import { PushPinIcon } from '@/components/ui/Icons';
import { isMessengerImageMime } from '@/components/Messenger/shared/attachments/messengerImageMime';
import './MessengerPinnedBar.css';

function oneLine(text: string | null | undefined): string {
  return (text ?? '').trim().replace(/\s+/g, ' ');
}

function messagePreview(pin: MessengerPinnedMessage): string {
  const msg = pin.message;
  const body = oneLine(msg.body);
  if (body) return body;
  if (msg.file_url) return isMessengerImageMime(msg.mime_type) ? 'Изображение' : msg.file_name?.trim() || 'Файл';
  if (Array.isArray(msg.context_attachments) && msg.context_attachments.length > 0) return 'Вложение';
  return 'Сообщение';
}

type MessengerPinnedBarProps = {
  pins: MessengerPinnedMessage[];
  onJumpToMessage: (messageId: string) => void;
};

export function MessengerPinnedBar({ pins, onJumpToMessage }: MessengerPinnedBarProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const activePin = pins[activeIndex] ?? pins[0] ?? null;

  useEffect(() => {
    setActiveIndex((prev) => (pins.length > 0 ? Math.min(prev, pins.length - 1) : 0));
  }, [pins.length]);

  const activePreview = useMemo(() => (activePin ? messagePreview(activePin) : ''), [activePin]);

  if (!activePin) return null;

  const jumpAndAdvance = () => {
    onJumpToMessage(activePin.message.id);
    setActiveIndex((prev) => (pins.length > 0 ? (prev + 1) % pins.length : 0));
  };

  return (
    <button
      type="button"
      className="messengerPinnedBar"
      onClick={jumpAndAdvance}
      title={activePreview}
      aria-label={`Перейти к закреплённому сообщению ${activeIndex + 1} из ${pins.length}`}
    >
      <span className="messengerPinnedBar__icon" aria-hidden>
        <PushPinIcon size={16} />
      </span>
      <span className="messengerPinnedBar__text">
        <span className="messengerPinnedBar__title">
          {pins.length > 1 ? 'Закреплённые сообщения' : 'Закреплённое сообщение'}
        </span>
        <span className="messengerPinnedBar__preview">{activePreview}</span>
      </span>
      <span className="messengerPinnedBar__counter" aria-hidden>
        {activeIndex + 1} из {pins.length}
      </span>
    </button>
  );
}
